/**
 * Turning the HTML of one mail message into something safe to put on our page.
 *
 * Split out of `MailBody` because it is the one part of the mail screen where a
 * mistake is a security bug rather than a layout one, and because it is where
 * the two kinds of message stop being symmetrical:
 *
 * - UNTRUSTED is anything a sender wrote, whether it arrived through the poller
 *   or was read live out of an IMAP folder. Scripts, event handlers and the
 *   presentational colour attributes go, and inline styles keep only a short
 *   list of harmless layout (`mailStyle.ts`: bold, gaps, thin lines): a
 *   stranger must not be able to run code in the CRM, repaint the page around
 *   their own message, or paint their text in a colour that only reads on the
 *   white background their mail assumed and our pane does not have.
 * - TRUSTED is our own HTML, built by our own code and already stored in our own
 *   database. Stripping `style` from it is not a defence, it is a bug.
 *   `composeHtml` in mail.routes.ts wraps every operator reply in
 *   `<div style="white-space:pre-wrap">`, so with styles removed a multi-line
 *   reply reads back as one run-on paragraph. A campaign letter is worse still:
 *   the letter builder puts every style inline on purpose, because Gmail drops
 *   `<style>` blocks, so the letter would render here as an unstyled stack of
 *   text and the operator would have no way to see what was actually sent.
 *
 * Scripts, event handlers, iframes and objects stay forbidden either way.
 * Nothing we generate needs them, so allowing them would only widen the blast
 * radius of a bug elsewhere that writes attacker text into an outbound row.
 *
 * Two things happen here that plain sanitising does not do, and both exist
 * because stripping `style` has consequences of its own:
 *
 * 1. Preheader text. Almost every marketing mail opens with a line written for
 *    the inbox preview and hidden from the message itself, with
 *    `display:none;font-size:1px;max-height:0`. Take the style attribute away
 *    and that line becomes the first thing in the message - so the element is
 *    dropped by looking at the style it had BEFORE the attribute is removed.
 * 2. Links. An `<a href>` inside the reading pane is a full page navigation, and
 *    the CRM is a single page app: following a link in a message threw away the
 *    whole workspace. Every untrusted link is retargeted to a new tab and given
 *    `rel="noopener noreferrer nofollow"`, so a message can neither reach back
 *    into our window nor lend us its ranking.
 *
 * Remote images are NOT blocked. Every image in a received message still loads,
 * tracking pixels included. Blocking them needs a "show images" control and a
 * per-sender memory to be anything other than annoying, and half a feature here
 * would be worse than none.
 */
import DOMPurify, { type UponSanitizeAttributeHookEvent } from "dompurify";
import { safeMailStyle } from "./mailStyle";

/**
 * Untrusted sender HTML: no scripts, no handlers, no styling of our page.
 *
 * `bgcolor`, `background`, `color` and `<font>` go on the way out. They are
 * the colour half of `style` by an older spelling, and leaving them behind was
 * the reason a message written as white text on a coloured cell arrived here
 * as white text on our own surface - invisible, with nothing on screen to say
 * anything had been removed.
 *
 * `style` itself is NOT in the list: it is cut down to `safeMailStyle` by the
 * `keepSafeStyle` hook, which `sanitizeMailHtml` installs around every use of
 * this config. Used without that hook, this config would keep a sender's
 * whole style, so it stays private to this file.
 */
const UNTRUSTED_SANITIZE = {
  FORBID_TAGS: ["style", "script", "iframe", "object", "embed", "form", "font"],
  // `class` and `id` go with them, and for the same reason rather than as
  // tidiness. This app ships a global utility stylesheet, so a sender who keeps
  // `class` does not need `style` at all: `class="fixed inset-0 z-50
  // bg-background"` on a div is a full-screen overlay painted over the CRM in
  // the CRM's own colours, which is the phishing surface stripping `style` was
  // meant to close. `id` follows because it is a hook for the same trick
  // through `:target` and for colliding with our own anchors.
  FORBID_ATTR: ["class", "id", "bgcolor", "background", "color"],
};

/** Our own HTML: keep the inline styling that carries the layout, drop
 *  everything executable. */
const TRUSTED_SANITIZE = {
  FORBID_TAGS: ["script", "iframe", "object", "embed", "form"],
  ALLOWED_ATTR: [
    "style",
    "class",
    "href",
    "src",
    "alt",
    "title",
    "width",
    "height",
    "align",
    "valign",
    "colspan",
    "rowspan",
    "bgcolor",
    "target",
    "rel",
    "border",
    "cellpadding",
    "cellspacing",
  ],
};

/**
 * The ways a mail hides its preheader.
 *
 * Deliberately a list of separate patterns rather than one clever expression:
 * each line is a real thing seen in real mail, and a new one should be cheap to
 * add without re-reading the others. A hit on any of them is enough - senders
 * stack four or five of these on the same element precisely because no single
 * one works in every client.
 */
const HIDDEN_STYLE_PATTERNS: readonly RegExp[] = [
  /display\s*:\s*none/i,
  /visibility\s*:\s*hidden/i,
  /mso-hide\s*:\s*all/i,
  /opacity\s*:\s*0(?:\.0+)?\s*(?:;|!|$)/i,
  /max-height\s*:\s*0(?:px|pt|em|rem)?\s*(?:;|!|$)/i,
  /max-width\s*:\s*0(?:px|pt|em|rem)?\s*(?:;|!|$)/i,
  // Zero, or the 1px that clients which ignore zero are given instead.
  /font-size\s*:\s*0(?:\.\d+)?\s*(?:px|pt|em|rem)?\s*(?:;|!|$)/i,
  /font-size\s*:\s*1\s*px\s*(?:;|!|$)/i,
];

/**
 * Was this element written to be invisible?
 *
 * Pure and exported so it can be tested without a DOM: everything else in this
 * file needs a browser, and this is the part with the interesting edge cases
 * (`font-size:10px` must not read as `font-size:1px`).
 */
export function isHiddenByStyle(style: string | null | undefined): boolean {
  if (!style) return false;
  return HIDDEN_STYLE_PATTERNS.some((re) => re.test(style));
}

/** Elements that carry no text and must survive even with a zeroed style. */
const NEVER_DROP = new Set(["HTML", "HEAD", "BODY", "IMG", "BR", "HR"]);

/**
 * Drop what the sender hid, before the style attribute that hid it is removed.
 *
 * `uponSanitizeElement` is the one hook that runs while the element still has
 * its original attributes, which is exactly what this needs. Images are spared:
 * a zero-size image is a tracking pixel, and removing it changes what the sender
 * learns about us in a way the operator did not ask for.
 */
function dropHiddenPreheader(node: Node): void {
  const el = node as Element;
  if (typeof el.getAttribute !== "function") return;
  if (NEVER_DROP.has(el.tagName)) return;
  if (!isHiddenByStyle(el.getAttribute("style"))) return;
  el.parentNode?.removeChild(el);
}

/**
 * Cut a sender's inline style down to the harmless layout list, or drop it.
 * It runs after `dropHiddenPreheader` has read the original style, so a hidden
 * preheader is still found by what hid it.
 */
function keepSafeStyle(_node: Element, data: UponSanitizeAttributeHookEvent): void {
  if (data.attrName !== "style") return;
  const safe = safeMailStyle(data.attrValue);
  if (safe) data.attrValue = safe;
  else data.keepAttr = false;
}

/** Every link leaves in its own tab, and lends us nothing on the way out. */
function retargetLinks(node: Node): void {
  const el = node as Element;
  if (typeof el.setAttribute !== "function") return;
  if (el.tagName !== "A") return;
  el.setAttribute("target", "_blank");
  el.setAttribute("rel", "noopener noreferrer nofollow");
}

/**
 * Sanitise one message body for display. `trusted` is true only for HTML this
 * CRM generated itself.
 *
 * The hooks are added and removed around this single synchronous call rather
 * than registered once at module load: DOMPurify keeps them on a global, and a
 * rule for sender mail left installed would start rewriting our own campaign
 * letters the next time one is drawn.
 */
export function sanitizeMailHtml(html: string, trusted: boolean): string {
  if (trusted) return DOMPurify.sanitize(html, TRUSTED_SANITIZE);
  DOMPurify.addHook("uponSanitizeElement", dropHiddenPreheader);
  DOMPurify.addHook("uponSanitizeAttribute", keepSafeStyle);
  DOMPurify.addHook("afterSanitizeAttributes", retargetLinks);
  try {
    return DOMPurify.sanitize(html, UNTRUSTED_SANITIZE);
  } finally {
    DOMPurify.removeHook("uponSanitizeElement");
    DOMPurify.removeHook("uponSanitizeAttribute");
    DOMPurify.removeHook("afterSanitizeAttributes");
  }
}
